import appClient from '../../common/services/AppClient';
import { CancelToken } from 'axios';
import {
    RenewalSuggestionsStateDto,
    RenewalSuggestionRequest,
    ContractSubmissionRequestStateDto,
    BaseCompanySegregationStateDto,
    BaseCompanyDto,
    NodeBaseDto,
    PreviousContractResultDto,
    FetchPreviousContractDataRequest,
    ContractComparisonSummaryResultDto,
    ConfirmContractComparisonSectionMatchesRequest
} from '../types';
import { BusinessPartnerDto, BusinessRequest, BusinessDto, CustomSessionRuntimeData } from '../../custom_shared/types';
import { endpoints } from '../../../config';

export default class ContractsIngestionService {
    async getRenewalSuggestionsState(sessionExtension: string, sessionId: string) {
        const url =
            endpoints.iotaUrl + `contracts-ingestion/renewal-suggestions-state/${sessionExtension}/${sessionId}`;

        return await appClient.get<RenewalSuggestionsStateDto>(url);
    }

    async searchRenewalSuggestions(
        sessionExtension: string,
        sessionId: string,
        suggestionsRequests: Record<string, RenewalSuggestionRequest>,
        cancelToken: CancelToken
    ) {
        const url =
            endpoints.iotaUrl + `contracts-ingestion/renewal-suggestions-search/${sessionExtension}/${sessionId}`;

        return await appClient.post<RenewalSuggestionsStateDto>(url, suggestionsRequests, 'json', cancelToken);
    }

    async disableRenewalSuggestionsSearch(sessionExtension: string, sessionId: string) {
        const url =
            endpoints.iotaUrl +
            `contracts-ingestion/renewal-suggestions-disable-search/${sessionExtension}/${sessionId}`;

        return await appClient.post<RenewalSuggestionsStateDto>(url);
    }

    async searchBusinessPartners(sessionExtension: string, sessionId: string, ignoreCache: boolean = false) {
        const url =
            endpoints.iotaUrl +
            `contracts-ingestion/search-business-partners/${sessionExtension}/${sessionId}?ignoreCache=${ignoreCache}`;

        return await appClient.post<BusinessPartnerDto[]>(url);
    }

    async searchBusinesses(sessionExtension: string, sessionId: string, request: BusinessRequest) {
        const url = endpoints.iotaUrl + `contracts-ingestion/search-businesses/${sessionExtension}/${sessionId}`;

        return await appClient.post<BusinessDto[]>(url, request);
    }

    async getContractSubmissionRequestState(sessionExtension: string, sessionId: string) {
        const url =
            endpoints.iotaUrl +
            `contracts-ingestion/contract-submission-request-state/${sessionExtension}/${sessionId}`;

        return await appClient.get<ContractSubmissionRequestStateDto>(url);
    }

    async getBaseCompanySegregationState(sessionExtension: string, sessionId: string) {
        const url =
            endpoints.iotaUrl + `contracts-ingestion/base-company-segregation-state/${sessionExtension}/${sessionId}`;

        return await appClient.get<BaseCompanySegregationStateDto>(url);
    }

    async getBaseCompanies(extension: string, sessionId: string) {
        const url = endpoints.iotaUrl + `contracts-ingestion/base-companies/${extension}/${sessionId}`;

        return await appClient.get<BaseCompanyDto[]>(url);
    }

    async selectBaseCompany(extension: string, sessionId: string, selectedBaseCompanyId: string) {
        const url = endpoints.iotaUrl + `contracts-ingestion/select-base-company/${extension}/${sessionId}`;

        return await appClient.post<BaseCompanySegregationStateDto>(url, { selectedBaseCompanyId });
    }

    async fetchPreviousContractData(
        extension: string,
        sessionId: string,
        request: FetchPreviousContractDataRequest,
        cancelToken?: CancelToken
    ) {
        const url = endpoints.iotaUrl + `contracts-ingestion/fetch-previous-contract-data/${extension}/${sessionId}`;

        return await appClient.post<PreviousContractResultDto>(url, request, 'json', cancelToken);
    }

    async executeContractComparisonAction(
        extension: string,
        sessionId: string,
        action: string,
        runtimeData: CustomSessionRuntimeData
    ) {
        const url = endpoints.iotaUrl + `contracts-ingestion/contract-comparison/action/${extension}/${sessionId}`;
        return await appClient.post<NodeBaseDto[]>(url, { action, runtimeData });
    }

    async confirmContractComparisonSectionMatches(
        extension: string,
        sessionId: string,
        request: ConfirmContractComparisonSectionMatchesRequest
    ) {
        const url =
            endpoints.iotaUrl + `contracts-ingestion/contract-comparison/section-matches/${extension}/${sessionId}`;

        return await appClient.post<void>(url, request);
    }

    async generateContractComparisonSummary(
        extension: string,
        sessionId: string,
        payload: string,
        cancelToken?: CancelToken
    ) {
        const url = endpoints.iotaUrl + `contracts-ingestion/contract-comparison-summary/${extension}/${sessionId}`;

        return await appClient.post<ContractComparisonSummaryResultDto>(url, { payload }, 'json', cancelToken);
    }
}
